/**
 * ShareCodeInput Component
 * 
 * UI component for entering 4-character share codes to join multiplayer sessions.
 * Provides input validation, formatting, and submission handling.
 */

import { GameSession } from '../../multiplayer/services/GameSession';
import './ShareCodeInput.css';

export interface ShareCodeInputOptions {
  onSuccess?: (shareCode: string) => void;
  onError?: (message: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  displayName?: string;
}

export class ShareCodeInput {
  private element!: HTMLElement;
  private inputElement!: HTMLInputElement;
  private submitButton!: HTMLButtonElement;
  private cancelButton!: HTMLButtonElement;
  private statusElement!: HTMLElement;
  private gameSession: GameSession;
  private options: ShareCodeInputOptions;
  private isVisible: boolean = false;
  private isLoading: boolean = false;

  constructor(options: ShareCodeInputOptions = {}) {
    this.options = options;
    this.gameSession = new GameSession();
    this.createElement();
    this.setupEventHandlers();
    this.setupGameSessionCallbacks();
  }

  // ==================== Element Creation ====================

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.className = 'share-code-input-overlay';
    this.element.innerHTML = `
      <div class="share-code-input-modal">
        <div class="share-code-input-header">
          <h2>Join Game</h2>
          <button class="close-button" type="button">×</button>
        </div>
        
        <div class="share-code-input-content">
          <label for="share-code-field" class="share-code-label">
            Enter 4-character share code:
          </label>
          
          <div class="share-code-input-group">
            <input 
              type="text" 
              id="share-code-field"
              class="share-code-field" 
              placeholder="${this.options.placeholder || 'ABCD'}"
              maxlength="4"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="characters"
              spellcheck="false"
            />
            <span class="share-code-format-guide">4 letters</span>
          </div>
          
          <div class="share-code-status" role="alert">
            <!-- Status messages appear here -->
          </div>
          
          <div class="share-code-actions">
            <button type="button" class="cancel-button">Cancel</button>
            <button type="submit" class="submit-button" disabled>Join Game</button>
          </div>
        </div>
        
        <div class="share-code-loading">
          <div class="loading-spinner"></div>
          <span>Joining game...</span>
        </div>
      </div>
    `;

    // Cache frequently used elements
    this.inputElement = this.element.querySelector('.share-code-field') as HTMLInputElement;
    this.submitButton = this.element.querySelector('.submit-button') as HTMLButtonElement;
    this.cancelButton = this.element.querySelector('.cancel-button') as HTMLButtonElement;
    this.statusElement = this.element.querySelector('.share-code-status') as HTMLElement;

    // Add to document
    document.body.appendChild(this.element);
  }

  // ==================== Event Handlers ====================

  private setupEventHandlers(): void {
    // Input field events
    this.inputElement.addEventListener('input', (e) => {
      this.handleInput(e as InputEvent);
    });

    this.inputElement.addEventListener('keydown', (e) => {
      this.handleKeyDown(e as KeyboardEvent);
    });

    this.inputElement.addEventListener('paste', (e) => {
      this.handlePaste(e as ClipboardEvent);
    });

    // Button events
    this.submitButton.addEventListener('click', () => {
      this.handleSubmit();
    });

    this.cancelButton.addEventListener('click', () => {
      this.handleCancel();
    });

    // Close button
    const closeButton = this.element.querySelector('.close-button') as HTMLButtonElement;
    closeButton.addEventListener('click', () => {
      this.handleCancel();
    });

    // Modal overlay click (close on outside click)
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.handleCancel();
      }
    });

    // Escape key handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.handleCancel();
      }
    });
  }

  // ==================== Share Code Normalization ====================

  /**
   * Normalizes share code input from different clients/keyboards/copy sources.
   * - Trims whitespace and separators (space, hyphen, underscore)
   * - Uppercases for canonical form
   * - Maps visually ambiguous digits to letters (0->O, 1->I, 5->S)
   * - Keeps only A-Z letters and caps length at 4
   */
  private normalizeShareCode(rawValue: string): string {
    const collapsed = rawValue
      .normalize('NFKC')
      .replace(/[\s\-_]+/g, '')
      .toUpperCase();

    const mapped = collapsed
      .replace(/0/g, 'O')
      .replace(/1/g, 'I')
      .replace(/5/g, 'S');

    return mapped.replace(/[^A-Z]/g, '').slice(0, 4);
  }

  private handleInput(_event: InputEvent): void {
    if (this.isLoading) return;

    const input = this.inputElement;
    const value = this.normalizeShareCode(input.value);
    
    // Update input value
    input.value = value;

    // Update validation and submit button state
    this.validateInput(value);
    this.updateSubmitButton(value);
    this.clearStatus();
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.isLoading) return;

    // Submit on Enter if valid
    if (event.key === 'Enter' && this.isValidShareCode(this.inputElement.value)) {
      event.preventDefault();
      this.handleSubmit();
      return;
    }

    // Allow navigation and editing keys
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 
      'Home', 'End', 'Tab', 'Escape', 'Enter'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Only allow letters for content
    if (!/^[A-Za-z]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private handlePaste(event: ClipboardEvent): void {
    if (this.isLoading) return;

    event.preventDefault();
    
    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanText = this.normalizeShareCode(pastedText);
    
    this.inputElement.value = cleanText;
    this.validateInput(cleanText);
    this.updateSubmitButton(cleanText);
    this.clearStatus();
  }

  private async handleSubmit(): Promise<void> {
    if (this.isLoading) return;

    const shareCode = this.normalizeShareCode(this.inputElement.value);
    this.inputElement.value = shareCode;
    if (!this.isValidShareCode(shareCode)) {
      this.showError('Please enter a 4-letter code');
      return;
    }

    const displayName = this.options.displayName || 'Player';
    
    try {
      this.setLoading(true);
      this.showStatus('Joining game...', 'loading');

      await this.gameSession.joinSession({
        shareCode,
        displayName
      });

      // Success handling is done in the GameSession callbacks

    } catch (error) {
      this.setLoading(false);
      const message = error instanceof Error ? error.message : 'Failed to join game';
      this.showError(message);
      console.error('[ShareCodeInput] Join error:', error);
    }
  }

  private handleCancel(): void {
    if (this.isLoading) return;
    this.hide();
    if (this.options.onCancel) {
      this.options.onCancel();
    }
  }

  // ==================== Game Session Callbacks ====================

  private setupGameSessionCallbacks(): void {
    this.gameSession.setCallbacks({
      onSessionJoined: (_data) => {
        this.setLoading(false);
        this.showStatus('Successfully joined game!', 'success');
        
        // Call success callback after brief delay to show success message
        setTimeout(() => {
          this.hide();
          if (this.options.onSuccess) {
            this.options.onSuccess(this.inputElement.value);
          }
        }, 1000);
      },

      onJoinError: (data) => {
        this.setLoading(false);
        let message = data.message;
        
        // Provide user-friendly error messages
        switch (data.error) {
          case 'SESSION_NOT_FOUND':
            message = 'Game not found. Check the code and try again.';
            break;
          case 'SESSION_FULL':
            message = 'Game is full. Try a different game.';
            break;
          case 'INVALID_CODE':
            message = 'Invalid game code. Please check and try again.';
            break;
          case 'ALREADY_IN_SESSION':
            message = 'You are already in a game.';
            break;
          default:
            message = message || 'Unable to join game. Please try again.';
        }
        
        this.showError(message);
        
        if (this.options.onError) {
          this.options.onError(message);
        }
      },

      onConnectionLost: () => {
        if (this.isLoading) {
          this.setLoading(false);
          this.showError('Connection lost. Please check your internet and try again.');
        }
      }
    });
  }

  // ==================== Validation ====================

  private isValidShareCode(code: string): boolean {
    return /^[A-Z]{4}$/.test(code);
  }

  private validateInput(value: string): void {
    const input = this.inputElement;
    
    if (value.length === 0) {
      input.classList.remove('valid', 'invalid');
    } else if (this.isValidShareCode(value)) {
      input.classList.remove('invalid');
      input.classList.add('valid');
    } else if (value.length === 4) {
      input.classList.remove('valid');
      input.classList.add('invalid');
    } else {
      input.classList.remove('valid', 'invalid');
    }
  }

  private updateSubmitButton(value: string): void {
    this.submitButton.disabled = !this.isValidShareCode(value) || this.isLoading;
  }

  // ==================== Status Display ====================

  private showStatus(message: string, type: 'loading' | 'error' | 'success' = 'loading'): void {
    this.statusElement.textContent = message;
    this.statusElement.className = `share-code-status ${type}`;
    this.statusElement.style.display = 'block';
  }

  private showError(message: string): void {
    this.showStatus(message, 'error');
  }

  private clearStatus(): void {
    this.statusElement.style.display = 'none';
    this.statusElement.className = 'share-code-status';
  }

  // ==================== Loading State ====================

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    
    const content = this.element.querySelector('.share-code-input-content') as HTMLElement;
    const loadingElement = this.element.querySelector('.share-code-loading') as HTMLElement;
    
    if (loading) {
      content.style.display = 'none';
      loadingElement.style.display = 'block';
      this.inputElement.disabled = true;
      this.submitButton.disabled = true;
    } else {
      content.style.display = 'block';
      loadingElement.style.display = 'none';
      this.inputElement.disabled = false;
      this.updateSubmitButton(this.inputElement.value);
    }
  }

  // ==================== Public Interface ====================

  public show(): void {
    this.isVisible = true;
    this.element.style.display = 'flex';
    this.inputElement.focus();
    this.clearStatus();
    
    // Reset form state
    this.inputElement.value = '';
    this.updateSubmitButton('');
    this.setLoading(false);
  }

  public hide(): void {
    this.isVisible = false;
    this.element.style.display = 'none';
    this.clearStatus();
  }

  public isShown(): boolean {
    return this.isVisible;
  }

  public setDisplayName(displayName: string): void {
    this.options.displayName = displayName;
  }

  public getShareCode(): string {
    return this.normalizeShareCode(this.inputElement.value);
  }

  public focus(): void {
    if (this.isVisible) {
      this.inputElement.focus();
    }
  }

  // ==================== Cleanup ====================

  public destroy(): void {
    this.gameSession.cleanup();
    this.element.remove();
  }
}